import React, { useEffect, useState } from "react";
import AuthLayout from "../AuthLayout";
import styles from "./resetpassword.module.scss";
import Button from "../../Shared/Button";
import { useLocation, useNavigate } from "react-router-dom";
import EyeClose from "../../../assets/images/eye-close.svg";
import EyeOpen from "../../../assets/images/eye-open.svg";
import { useFormik } from "formik";
import * as yup from "yup";
import { resendVerification, resetPassword } from "../../../helpers/service";
import { toast } from "react-toastify";
import AuthTextField from "../AuthTextField";
import AuthAlerts from "../Signup/AuthAlerts";

const ResetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .min(8, "Your password is not strong enough. Use at least 8 characters")
    .required("Required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Required"),
});

const ResetPassword = () => {
  const location = useLocation();
  const isVerified = location?.state?.isVerified || false;
  const [isEmailNotVerified, setIsEmailNotVerified] = useState(false);
  const email = location?.state?.email || "";
  const accessToken = location?.state?.accessToken || "";
  const navigate = useNavigate();
  const [passwordToggle, setPasswordToggle] = useState(false);
  const [confirmPasswordToggle, setConfirmPasswordToggle] =
    React.useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: ResetPasswordSchema,
    onSubmit: (values) => {
      const data = {
        new_password: values.password,
        confirm_password: values.confirmPassword,
      };
      localStorage.setItem("accessToken", accessToken);
      handleSubmitResetPassword(data);
    },
  });

  const handleSubmitResetPassword = async (values) => {
    try {
      await resetPassword(values);

      toast.success(
        "Password reset successfully. Please login with new password"
      );
      formik.setSubmitting(false);
      localStorage.removeItem("accessToken");
      navigate("/");
    } catch (error) {
      const responseObject = error?.response || {};
      const errorStatus = responseObject?.status;
      const errorMessage = responseObject?.data?.message || null;
      const notVerified =
        (errorMessage?.includes("not been verified") && errorStatus === 404) ||
        false;
      formik.setSubmitting(false);
      localStorage.removeItem("accessToken");
      if (notVerified) {
        setIsEmailNotVerified(true);
      } else {
        toast.error(
          errorMessage ? errorMessage : "Something went wrong. Please try again"
        );
      }
    }
  };
  const handleResendVerification = async () => {
    const submitData = {
      email: formik?.values?.email,
    };
    try {
      const response = await resendVerification(submitData);
      toast.success(response?.message || "Email sent. Please check your inbox");
    
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };
  const emailDomain = `https://${email?.split("@")[1]}`;

  useEffect(() => {
    if (!isVerified) {
      navigate("/");
    }
  }, [isVerified]);

  return (
    <AuthLayout>
      {isEmailNotVerified ? (
        <>
          <AuthAlerts
            accountCreatedButNotVerified
            buttonOnClick={() => {
              window.open(emailDomain, "_blank");
            }}
            linkOnClick={() => {
              handleResendVerification();
            }}
          />
        </>
      ) : (
        <>
          <div className={styles["reset_password_container"]}>
            <div className={styles["reset_password_text"]}>Reset Password</div>
            <div className={styles["label_container"]}>
              Create a new strong password
            </div>
            <div className={styles["email_field_container"]}>
              <AuthTextField
                heading="Password"
                icon={passwordToggle ? EyeOpen : EyeClose}
                iconOnClick={() => {
                  setPasswordToggle(!passwordToggle);
                }}
                type={passwordToggle ? "text" : "password"}
                onChange={formik.handleChange}
                name="password"
                error={formik?.errors?.password}
                touched={formik?.touched?.password}
                disabled={formik.isSubmitting}
              />
              <div className={`${styles["confirm_password_container"]}`}>
                <AuthTextField
                  heading="Confirm password"
                  icon={confirmPasswordToggle ? EyeOpen : EyeClose}
                  iconOnClick={() => {
                    setConfirmPasswordToggle(!confirmPasswordToggle);
                  }}
                  type={confirmPasswordToggle ? "text" : "password"}
                  onChange={formik.handleChange}
                  name="confirmPassword"
                  error={formik?.errors?.confirmPassword}
                  touched={formik?.touched?.confirmPassword}
                  disabled={formik.isSubmitting}
                />
              </div>
              <div className={styles["submit_button_container"]}>
                <Button
                  type="submit"
                  className={styles["submit_button"]}
                  onClick={() => {
                    formik.submitForm();
                  }}
                  disabled={formik.isSubmitting}
                  text="Save"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
